import { useCallback, useEffect, useState } from "react";

type InitialValue<T> = T | (() => T);

const useLocalStorage = <T>(key: string | null | undefined, initialValue: InitialValue<T>): [T, (name: string, value: any) => void, () => void] => {
  const [value, setValue] = useState(() => {
    try {
      if (!key) {
        return initialValue;
      }

      const storedValue = localStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : initialValue;
    } catch (error) {
      console.error('Error reading value from `localStorage`:', error);
      return initialValue;
    }
  });

  const setLocalStorageValue = useCallback((name: string, value: any) => {
    try {
      setValue((prevValue: T) => {
        const newValue = { ...prevValue, [name]: value };

        if (key) {
          localStorage.setItem(key, JSON.stringify(newValue));

          window.dispatchEvent(new StorageEvent('storage', {
            key,
            newValue: JSON.stringify(newValue),
          }));
        }

        return newValue;
      });
    } catch (error) {
      console.error('Error saving value to `localStorage`:', error);
    }
  }, [key]);

  const removeLocalStorageValue = useCallback(() => {
    setValue(initialValue);

    if (key) {
      localStorage.removeItem(key);
    }
  }, [initialValue, key]);

  useEffect(() => {
    const updateAllTabs = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) {
        setValue(JSON.parse(event.newValue));
      }
    };

    if (key) {
      window.addEventListener('storage', updateAllTabs);
    }

    return () => {
      if (key) {
        window.removeEventListener('storage', updateAllTabs);
      }
    };
  }, [key]);

  return [value, setLocalStorageValue, removeLocalStorageValue];
};

export default useLocalStorage;